import { useContext, useMemo, useState, useEffect } from "react";
import { Panel } from "primereact/panel";
import { ExplainedButton as Button } from '../explained-button';
import { MultiSelect } from "primereact/multiselect";
import { InputText } from "primereact/inputtext";
import { CourseManagementDataContext } from "../../providers/course-management-provider";
import { UserDto } from "../../general/dtos";

export function CourseEditorUsersTab() {
    const ctx = useContext(CourseManagementDataContext);

    // Always define hooks before any return
    const course = ctx?.selectedCourseFull ?? null;
    const allUsers = ctx?.allUsers ?? [];

    const [selectedToAdd, setSelectedToAdd] = useState<string[]>([]);

    // ⭐ Search state
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    // ⭐ Debounce search input (150ms)
    useEffect(() => {
        const handle = setTimeout(() => setDebouncedSearch(search.trim().toLowerCase()), 150);
        return () => clearTimeout(handle);
    }, [search]);

    // ⭐ Filter helper
    const matchesSearch = (user: UserDto) => {
        if (!debouncedSearch) return true;
        const s = debouncedSearch;
        return (
            user.first_name.toLowerCase().includes(s) ||
            user.last_name.toLowerCase().includes(s) ||
            user.email.toLowerCase().includes(s)
        );
    };

    // ⭐ Enrolled users (filtered)
    const enrolledUsers = useMemo(() => {
        if (!course) return [];
        const enrolledIds = new Set(course.users.map(u => u.sub));
        return allUsers.filter(u => enrolledIds.has(u.sub) && matchesSearch(u));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [course, allUsers, debouncedSearch]);

    // ⭐ Available users (filtered)
    const availableUsers = useMemo(() => {
        if (!course) return [];
        const enrolledIds = new Set(course.users.map(u => u.sub));
        return allUsers
            .filter(u => !enrolledIds.has(u.sub) && matchesSearch(u))
            .map(u => ({
                label: `${u.first_name} ${u.last_name} (${u.email})`,
                value: u.sub
            }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [course, allUsers, debouncedSearch]);

    // After hooks, conditional returns are allowed
    if (!ctx) return null;
    if (!course) return <div className="text-gray-500 p-3">No course selected.</div>;

    return (
        <div className="flex flex-column gap-3 h-full">

            {/* ⭐ Search bar */}
            <div className="flex justify-content-end">
                <span className="p-input-icon-left w-6">
                    <i className="pi pi-search" />
                    <InputText
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Filter users (both enrolled and not)..."
                        className="w-full pl-5"
                    />
                </span>
            </div>

            <div className="flex gap-4 flex-grow-1">

                {/* Left: Enrolled users */}
                <div className="w-5 flex flex-column">
                    <Panel header="Enrolled users" className="flex flex-column flex-grow-1">

                        {enrolledUsers.length === 0 && (
                            <div className="text-gray-500">No matching users.</div>
                        )}

                        <div className="flex flex-column gap-2 overflow-auto" style={{ maxHeight: "100%" }}>
                            {enrolledUsers.map(user => (
                                <div
                                    key={user.sub}
                                    className="flex justify-content-between align-items-center p-2 border-1 surface-border border-round"
                                >
                                    <div className="flex flex-column">
                                        <strong>{user.first_name} {user.last_name}</strong>
                                        <small className="text-gray-600">{user.email}</small>
                                    </div>

                                    <Button
                                        icon="pi pi-times"
                                        className="p-button-rounded p-button-text p-button-danger"
                                        onClick={() => ctx.removeUserFromCourse(course.course_key, user.sub)}
                                    />
                                </div>
                            ))}
                        </div>

                    </Panel>
                </div>

                {/* Right: Add users */}
                <div className="flex-grow-1 flex flex-column">
                    <Panel header="Add users" className="flex flex-column flex-grow-1">

                        <div className="flex flex-column gap-3">

                            <MultiSelect
                                value={selectedToAdd}
                                options={availableUsers}
                                onChange={(e) => setSelectedToAdd(e.value)}
                                placeholder="Select users to add"
                                className="w-full"
                                display="chip"
                                filter={false} // we use our own search
                            />

                            <Button
                                label="Add selected users"
                                icon="pi pi-user-plus"
                                disabled={selectedToAdd.length === 0}
                                disabledReason="Select users in the Add users list first."
                                onClick={() => {
                                    ctx.addUsersToCourse(course.course_key, selectedToAdd);
                                    setSelectedToAdd([]);
                                }}
                            />

                        </div>

                    </Panel>
                </div>

            </div>
        </div>
    );
}
